import React, { useState } from 'react';
import AdminList, { NameCellRenderer, ArticleCountCellRenderer, ArticleViewCellRenderer } from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { TagItem } from '../../../shared/types';
import { makeStyles, createStyles } from '@material-ui/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import AdminListToolbar, { AdminListToolbarButton } from '../../ui/admin/AdminListToolbar';
import { TAGS } from '../../../gql/tags';
import { filterArrayOfObjectsBy, sortArray } from '../../../utils/sorting.utils';
import { useArticleFilter } from '../../providers/admin/ArticleFilterProvider';

const name = 'Tags';
const apiName = 'tags';

enum TagFilterType {
	All,
	EmptyTags
}

export const tagFilterTypeLabel = {
	[TagFilterType.All]: 'All',
	[TagFilterType.EmptyTags]: 'Empty tags'
};

const useStyles = makeStyles(() => createStyles({
	gridHeader: {
		fontWeight: 'bold'
	}
}));

const TagList = () => {
	const { loading, error, data } = useQuery(TAGS, { fetchPolicy: 'network-only' });
	const items: TagItem[] = data?.tags ?? [];

	const classes = useStyles();
	const articleFilter = useArticleFilter();
	const [filter, setFilter] = useState(TagFilterType.All);
	const [searchString, setSearchString] = useState('');

	const filteredTags: TagItem[] = filter !== TagFilterType.All ?
		items.filter((tag: TagItem) => tag.articleCount === 0) :
		items;

	const sortedRecords = sortArray(filterArrayOfObjectsBy(filteredTags, searchString, ['name', 'description']), 'name');

	const toolbarFilterButtons: AdminListToolbarButton[] = [
		{
			selected: filter === TagFilterType.All,
			onClick: (): void => setFilter(TagFilterType.All),
			label: tagFilterTypeLabel[TagFilterType.All]
		},
		{
			selected: filter === TagFilterType.EmptyTags,
			onClick: (): void => setFilter(TagFilterType.EmptyTags),
			label: tagFilterTypeLabel[TagFilterType.EmptyTags]
		}
	];

	const columnDefs: ColDef[] = [
		{
			colId: 'tag-name-col',
			headerName: 'Name',
			headerClass: classes.gridHeader,
			field: 'name',
			cellRendererFramework: (props: any): JSX.Element => <NameCellRenderer {...props} apiName={apiName} />,
			width: 260,
			menuTabs: []
		},
		{
			colId: 'tag-description-col',
			headerName: 'Description',
			headerClass: classes.gridHeader,
			field: 'description',
			width: 564,
			menuTabs: []
		},
		{
			colId: 'tag-articles-col',
			headerName: 'Articles',
			headerClass: classes.gridHeader,
			field: 'articleCount',
			cellRendererFramework: (props: ICellRendererParams) => <ArticleCountCellRenderer value={props.value} filter={articleFilter} context={props.context} filterData={{_id: props.data._id, name: props.data.name, type: 'tag'}} />,
			width: 160,
			menuTabs: []
		},
		{
			headerName: 'Views',
			field: 'views',
			width: 160,
			cellRendererFramework: (props: any) => <ArticleViewCellRenderer {...props} apiName={apiName} filter={articleFilter} linkTo="" />,
			menuTabs: []
		}
	];

	return (
		<AdminList
			name={name}
			items={sortedRecords}
			loading={loading}
			error={error}
			columns={columnDefs}
			showAdd={true}
			toolbar={
				<AdminListToolbar
					filterItems={toolbarFilterButtons}
					searchString={searchString}
					toolbarPlaceholder="Search tags..."
					onQueryStringChange={setSearchString}
				/>
			}
		/>
	);
};

export default TagList;
